//@ts-nocheck
import moment from "moment";
import { html } from "lit";
import { getCurrentTimezone, getTimeZoneIdentifier } from "./app-utils";
import { GlobalConstants } from "./constants";

/** Values below this (as ms) are treated as epoch **seconds** (10-digit API timestamps). */
export const EPOCH_SECONDS_THRESHOLD_MS = 10_000_000_000;

const WORKFLOW_TZ_DISPLAY_LABELS: Record<string, string> = {
  us_central: "US Central",
  us_eastern: "US Eastern",
  us_pacific: "US Pacific",
  us_mountain: "US Mountain",
  us_alaska: "US Alaska",
};

/**
 * Parse DynamoDB/C# epoch seconds, epoch ms, or ISO/date strings to a Date (UTC instant).
 * Returns null for empty/invalid input — callers decide fallback display.
 */
export function parseEpochOrIsoToDate(raw: unknown): Date | null {
  if (raw == null || raw === "") {
    return null;
  }

  if (typeof raw === "number") {
    if (!Number.isFinite(raw)) {
      return null;
    }
    const ms = raw < EPOCH_SECONDS_THRESHOLD_MS ? raw * 1000 : raw;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  let s = String(raw).trim();
  if (!s) {
    return null;
  }
  // " UTC" is a zone label (AuditProxy / UserProxy TimeZoneFormat); preserve as Z, not local.
  s = s.replace(/(Z|[+-]\d{2}:?\d{2})?\s+UTC\s*$/i, (_m, zone) => zone ?? "Z");
  s = s.replace(/^(\d{4}-\d{2}-\d{2}) (\d)/, "$1T$2");

  if (/^\d{10,13}$/.test(s)) {
    const n = parseInt(s, 10);
    const ms = n < EPOCH_SECONDS_THRESHOLD_MS ? n * 1000 : n;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const digitPrefix = s.match(/^(\d{10,13})/);
  if (digitPrefix) {
    const n = parseInt(digitPrefix[1], 10);
    const ms = n < EPOCH_SECONDS_THRESHOLD_MS ? n * 1000 : n;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  // User API LastSignOnDateTime: UTC clock as MM/dd/yyyy HH:mm:ss (not local).
  const canonicalUtc = s.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4}) (\d{1,2}):(\d{2}):(\d{2})$/,
  );
  if (canonicalUtc) {
    const month = parseInt(canonicalUtc[1], 10);
    const day = parseInt(canonicalUtc[2], 10);
    const year = parseInt(canonicalUtc[3], 10);
    const hour = parseInt(canonicalUtc[4], 10);
    const minute = parseInt(canonicalUtc[5], 10);
    const second = parseInt(canonicalUtc[6], 10);
    const d = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const parsedMs = Date.parse(s);
  if (!Number.isNaN(parsedMs)) {
    const d = new Date(parsedMs);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  return null;
}

/** True when viewer uses legacy India display format (DD-MM-YYYY HH:mm:ss). */
export function usesIndiaDateFormat(timeZone?: string): boolean {
  return (timeZone ?? getCurrentTimezone()).includes(GlobalConstants.TIMEZONE);
}

/**
 * Short TZ abbreviation for the viewer (CST/CDT, IST, etc.) via Intl — handles DST.
 * @param timeZone - IANA zone; defaults to browser zone.
 * @param atInstant - instant used to resolve DST; defaults to now.
 */
export function getViewerTimezoneAbbreviation(
  timeZone?: string,
  atInstant: Date = new Date(),
): string {
  const tz = timeZone ?? getCurrentTimezone();
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(atInstant);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
}

/**
 * Canonical datetime cell formatter (ACME-1351).
 * India browsers: DD-MM-YYYY HH:mm:ss; others: Intl en-US short date + medium time.
 */
export function formatDateTimeForDisplay(
  raw: unknown,
  timeZone?: string,
): string {
  const d = parseEpochOrIsoToDate(raw);
  if (!d) {
    if (raw == null || raw === "") {
      return "";
    }
    const s = String(raw).trim();
    return s || "";
  }

  const tz = timeZone ?? getCurrentTimezone();
  if (usesIndiaDateFormat(tz)) {
    return moment(d).format(GlobalConstants.TIME_FORMAT);
  }

  return new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    timeStyle: "medium",
    dateStyle: "short",
  }).format(d);
}

/** Tooltip datetime: local formatted value + TZ abbrev (standalone contexts only, e.g. scheduler toasts). */
export function formatDateTimeForTooltip(
  raw: unknown,
  timeZone?: string,
): string {
  const formatted = formatDateTimeForDisplay(raw, timeZone);
  if (!formatted) {
    return formatted;
  }
  const d = parseEpochOrIsoToDate(raw);
  const abbrev = getViewerTimezoneAbbreviation(timeZone, d ?? new Date());
  return abbrev ? `${formatted} ${abbrev}` : formatted;
}

/** Grid column header, e.g. `CREATED DATE (CST)`. */
export function dateTimeColumnLabel(
  baseLabel: string,
  timeZone?: string,
  atInstant?: Date,
): string {
  const abbrev = getViewerTimezoneAbbreviation(timeZone, atInstant);
  return abbrev ? `${baseLabel} (${abbrev})` : baseLabel;
}

/** Detail / form field label, e.g. `Created Date (CST)`. */
export function dateTimeFieldLabel(
  baseLabel: string,
  timeZone?: string,
  atInstant?: Date,
): string {
  return dateTimeColumnLabel(baseLabel, timeZone, atInstant);
}

/** Reporting filter label helper (same as column label). Prefer TZ on time fields only. */
export function dateTimeFilterLabel(
  baseLabel: string,
  timeZone?: string,
  atInstant?: Date,
): string {
  return dateTimeColumnLabel(baseLabel, timeZone, atInstant);
}

/** Page-level banner copy (Q1=C). */
export function getPageTimezoneBannerText(timeZone?: string): string {
  const abbrev = getViewerTimezoneAbbreviation(timeZone);
  if (!abbrev) {
    return "All times shown in your local time zone.";
  }
  return `All times shown in ${abbrev} (your local time zone).`;
}

/** Inline styles for grid timezone note — transparent, accent #77B6F5 (ACME-1351). */
export const EFM_TIMEZONE_BANNER_STYLE =
  "color:#77B6F5;background:transparent;margin:0;padding:0;font-size:0.875rem;line-height:1;";

/** On jha-advanced-table host — removes empty .controls top padding above column headers. */
export const EFM_TIMEZONE_GRID_TABLE_STYLE =
  "--jha-advanced-table-controls-padding:0;";

/**
 * Shadow-DOM CSS for jha-advanced-table on EFM timezone-grid pages (prepend inside
 * each view's injectAdvancedTableStyles).
 *
 * **Rollback:** delete this export and remove it from injectAdvancedTableStyles() in
 * Users, Workflows, Workflow Templates, Business Units, Institutions, Audit Details.
 *
 * **Why (ACME-1351 gap on deployed dev):** jha-advanced-table always renders
 * `div.controls` above column headers. When `filtersChanged` is true it injects
 * `jh-button#reset-button` ("Clear filters") even on pages that use *external* filters
 * only (Advance Filters, page search, etc.). With real API/session state that internal
 * map can be non-empty on deployed while local preview stays empty — ~56px row, often
 * invisible (`hide-label` on jh-button) but still reserved (`max-height: 76px`).
 *
 * **Not a jha-wc defect for in-table filter UIs** — expected for views that use built-in
 * column filters/search. EFM grids do not; collapse controls only when those slots are
 * absent (`:not(:has(jha-table-filter-group))`, `:not(:has(.search))`).
 *
 * **Future risk:** a page that adds in-table filters/search while keeping this snippet
 * will need to drop this block for that page only.
 */
export const EFM_TIMEZONE_GRID_TABLE_SHADOW_STYLES_TEXT = `
  div#table-header div.controls.flex-container-end:not(:has(jha-table-filter-group)):not(:has(.search)) {
    max-height: 0 !important;
    overflow: hidden !important;
    padding: 0 !important;
    margin: 0 !important;
    min-height: 0 !important;
  }
`;

/**
 * Lit snippet for reusable timezone banner (place directly above grid content).
 * `styleExtra` appends to the base inline style (e.g. `"padding-bottom:8px;"`).
 */
export function renderTimezoneBanner(timeZone?: string, styleExtra?: string) {
  const style = styleExtra
    ? `${EFM_TIMEZONE_BANNER_STYLE}${styleExtra}`
    : EFM_TIMEZONE_BANNER_STYLE;
  return html`<div
    class="efm-timezone-banner"
    role="note"
    style=${style}
  >
    ${getPageTimezoneBannerText(timeZone)}
  </div>`;
}

/**
 * Banner + grid as siblings (no flex wrapper — jha-advanced-table is height/overflow sensitive).
 * Use inside jha-card; keep ~4px gap via banner padding-bottom.
 */
export function renderGridWithTimezoneBanner(
  gridContent: unknown,
  timeZone?: string,
) {
  return html`${renderTimezoneBanner(timeZone, "padding-bottom:4px;")}${gridContent}`;
}

/** Scheduler field label using workflow timezone key (Q8), e.g. `Start Date (US Central)`. */
export function workflowTimezoneDisplayLabel(
  apiZoneKey: string | undefined | null,
): string {
  const key = String(apiZoneKey ?? "").trim().toLowerCase();
  if (!key) {
    return "";
  }
  return WORKFLOW_TZ_DISPLAY_LABELS[key] ?? key.replace(/_/g, " ");
}

export function schedulerFieldLabel(
  baseLabel: string,
  workflowApiZoneKey: string | undefined | null,
): string {
  const tzLabel = workflowTimezoneDisplayLabel(workflowApiZoneKey);
  return tzLabel ? `${baseLabel} (${tzLabel})` : baseLabel;
}

/** Normalize API values to epoch **seconds** for numeric grid sort. */
export function toEpochSecondsForSort(raw: unknown): unknown {
  if (raw == null || raw === "") {
    return raw;
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return raw < EPOCH_SECONDS_THRESHOLD_MS ? raw : Math.floor(raw / 1000);
  }
  const s = String(raw).trim();
  if (/^\d{10,13}$/.test(s)) {
    const n = parseInt(s, 10);
    return n < EPOCH_SECONDS_THRESHOLD_MS ? n : Math.floor(n / 1000);
  }
  const digitPrefix = s.match(/^(\d{10,13})/);
  if (digitPrefix) {
    const n = parseInt(digitPrefix[1], 10);
    return n < EPOCH_SECONDS_THRESHOLD_MS ? n : Math.floor(n / 1000);
  }
  const d = parseEpochOrIsoToDate(s);
  if (d) {
    return Math.floor(d.getTime() / 1000);
  }
  return raw;
}

export function normalizeEpochSeconds(raw: unknown): number {
  const v = toEpochSecondsForSort(raw);
  if (typeof v === "number" && Number.isFinite(v)) {
    return v;
  }
  if (v == null || v === "") {
    return 0;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function formatEpochSecondsForDisplay(
  epochSec: number,
  timeZone?: string,
): string {
  if (epochSec == null || !Number.isFinite(epochSec) || epochSec <= 0) {
    return "";
  }
  return formatDateTimeForDisplay(Math.floor(epochSec), timeZone);
}

export const DURATION_COLUMN_LABEL = "DURATION";
export const DURATION_COLUMN_HEADER_TOOLTIP =
  "Shown as HH:MM:SS when hours are present, MM:SS otherwise, or 0:SS(.mmm) for sub-minute durations.";

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** Workflow duration in seconds (fractional allowed); null for empty or invalid. Zero is valid. */
export function resolveDurationSeconds(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === "") {
    return null;
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) {
    return null;
  }
  return n;
}

function splitDurationParts(totalSeconds: number): {
  hours: number;
  minutes: number;
  seconds: number;
} {
  const totalMs = Math.round(totalSeconds * 1000);
  const hours = Math.floor(totalMs / 3_600_000);
  const afterHours = totalMs % 3_600_000;
  const minutes = Math.floor(afterHours / 60_000);
  const seconds = (afterHours % 60_000) / 1000;
  return { hours, minutes, seconds };
}

/** Seconds segment with optional millisecond precision (e.g. 06.335). */
function formatDurationSecondPart(seconds: number): string {
  const roundedMs = Math.round(seconds * 1000);
  const whole = Math.floor(roundedMs / 1000);
  const ms = roundedMs % 1000;
  const wholeStr = pad2(whole);
  if (ms === 0) {
    return wholeStr;
  }
  const frac = String(ms).padStart(3, "0").replace(/0+$/, "");
  return `${wholeStr}.${frac}`;
}

function formatDurationSecondsValue(seconds: number): string {
  const roundedMs = Math.round(seconds * 1000);
  const whole = Math.floor(roundedMs / 1000);
  const ms = roundedMs % 1000;
  if (ms === 0) {
    return String(whole);
  }
  const frac = String(ms).padStart(3, "0").replace(/0+$/, "");
  return `${whole}.${frac}`;
}

/** Grid / export cell text: 0:SS(.mmm), M:SS(.mmm), or HH:MM:SS(.mmm) (ACME-1528). */
export function formatDurationSecondsDisplay(totalSeconds: number): string {
  const { hours, minutes, seconds } = splitDurationParts(totalSeconds);

  if (hours > 0) {
    return `${pad2(hours)}:${pad2(minutes)}:${formatDurationSecondPart(seconds)}`;
  }
  if (minutes > 0) {
    return `${minutes}:${formatDurationSecondPart(seconds)}`;
  }
  return `0:${formatDurationSecondPart(seconds)}`;
}

/** Hover tooltip prose for workflow duration (ACME-1528). */
export function formatDurationSecondsTooltip(totalSeconds: number): string {
  const { hours, minutes, seconds } = splitDurationParts(totalSeconds);
  const hasFraction = Math.round(seconds * 1000) % 1000 !== 0;

  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours} hour${hours === 1 ? "" : "s"}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} minute${minutes === 1 ? "" : "s"}`);
  }
  if (seconds > 0 || parts.length === 0 || hasFraction) {
    const secLabel = formatDurationSecondsValue(seconds);
    parts.push(`${secLabel} second${secLabel === "1" ? "" : "s"}`);
  }

  if (parts.length === 1) {
    return parts[0];
  }
  if (parts.length === 2) {
    return `${parts[0]} and ${parts[1]}`;
  }
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
}

export function formatDurationSeconds(
  raw: unknown,
): { display: string; tooltip: string } | null {
  const seconds = resolveDurationSeconds(raw);
  if (seconds === null) {
    return null;
  }
  return {
    display: formatDurationSecondsDisplay(seconds),
    tooltip: formatDurationSecondsTooltip(seconds),
  };
}

export function formatAuditTransDuration(
  transDuration: unknown,
  transTime?: unknown,
): { display: string; tooltip: string } | null {
  const raw =
    transDuration !== undefined && transDuration !== null && transDuration !== ""
      ? transDuration
      : transTime;
  return formatDurationSeconds(raw);
}

export function isInProgressAuditStatus(
  status: string | null | undefined,
): boolean {
  return String(status ?? "").trim().toLowerCase() === "init";
}

/** Duration label when transDuration/transTime are empty (grid + CSV). */
export function getAuditTransDurationEmptyLabel(
  status: string | null | undefined,
): string | null {
  return isInProgressAuditStatus(status) ? "Processing" : null;
}

/** jha-advanced-table header title for the duration column. */
export function applyDurationColumnHeaderTooltip(
  tableShadowRoot: ShadowRoot | null | undefined,
) {
  if (!tableShadowRoot) {
    return;
  }
  const headerBtn = tableShadowRoot.querySelector(
    "jha-table-header#transDuration .sort-button",
  ) as HTMLElement | null;
  if (headerBtn) {
    headerBtn.setAttribute("title", DURATION_COLUMN_HEADER_TOOLTIP);
  }
}

/** Current instant formatted in workflow scheduler timezone (validation toasts, Q8). */
export function formatWorkflowSchedulerNow(
  workflowApiZoneKey: string | undefined | null,
): string {
  const iana = getTimeZoneIdentifier(String(workflowApiZoneKey ?? ""));
  return formatDateTimeForTooltip(Date.now(), iana || undefined);
}

/** CSV export filename with viewer TZ abbrev (Q10). */
export function buildCsvExportFilename(prefix: string, atInstant: Date = new Date()): string {
  const abbrev = getViewerTimezoneAbbreviation(undefined, atInstant);
  const datePart = new Intl.DateTimeFormat("en-CA", {
    timeZone: getCurrentTimezone(),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(atInstant);
  const safeAbbrev = abbrev.replace(/\s+/g, "") || "local";
  return `${prefix}_${datePart}_${safeAbbrev}.csv`;
}

export function instantMsFromRaw(raw: unknown): number | null {
  const d = parseEpochOrIsoToDate(raw);
  return d ? d.getTime() : null;
}
